import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { watchlistApi } from "@/api/ux";
import { cn } from "@/design-system/lib/utils";

export interface StarButtonProps {
  entityType: string;
  entityId: string;
  entityTitle?: string | null;
  size?: number;
  className?: string;
  /** Render as an inline icon only (smaller). Defaults to true. */
  inline?: boolean;
  /**
   * Pre-fetched watch status from a page-level useWatchlistStatuses batch.
   * When provided the per-row GET /watchlist/{type}/{id} query is skipped —
   * list pages MUST pass this or each row fires its own request (the broker
   * list N+1). Detail pages omit it and keep the single-status query.
   */
  isWatching?: boolean;
}

/**
 * UX Enhancements §5. Optimistic star/unstar toggle. Invalidates the
 * user's watchlist query on mutation so the dashboard widget re-renders
 * the next time it's visible.
 */
export function StarButton({
  entityType,
  entityId,
  entityTitle,
  size = 18,
  className,
  inline = true,
  isWatching: prefetched,
}: StarButtonProps) {
  const queryClient = useQueryClient();

  // When the status is batch-fed, the prop only updates after the page-level
  // query refetches (the onSettled invalidation below) — the local override
  // keeps the toggle feeling instant in the meantime.
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const watchingQuery = useQuery({
    queryKey: ["watchlist", "is", entityType, entityId],
    queryFn: () => watchlistApi.isWatching(entityType, entityId),
    enabled: prefetched === undefined,
  });

  const mutation = useMutation({
    mutationFn: async (next: boolean) => {
      if (next) {
        await watchlistApi.add({ entityType, entityId, entityTitle });
      } else {
        await watchlistApi.remove(entityType, entityId);
      }
      return next;
    },
    onMutate: async (next: boolean) => {
      setOptimistic(next);
      await queryClient.cancelQueries({ queryKey: ["watchlist", "is", entityType, entityId] });
      const previous = queryClient.getQueryData<boolean>(["watchlist", "is", entityType, entityId]);
      queryClient.setQueryData(["watchlist", "is", entityType, entityId], next);
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      setOptimistic(null);
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(["watchlist", "is", entityType, entityId], ctx.previous);
      }
    },
    onSettled: () => {
      // Prefix match also invalidates the page-level ["watchlist","statuses",…]
      // batch, refreshing any prop-fed rows.
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
    },
  });

  const isWatching = optimistic ?? prefetched ?? watchingQuery.data ?? false;
  const loading = watchingQuery.isLoading || mutation.isPending;
  const label = isWatching ? "Remove from watchlist" : "Add to watchlist";

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={isWatching}
      title={label}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!loading) mutation.mutate(!isWatching);
      }}
      disabled={loading}
      className={cn(
        "inline-flex items-center justify-center",
        inline ? "h-7 w-7 rounded-sm" : "h-9 w-9 rounded-md border border-border-light",
        "bg-transparent transition-colors duration-fast ease-standard",
        "hover:bg-gold-highlight/10 focus-visible:outline-none focus-visible:shadow-goldRing",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        className
      )}
    >
      <Star
        size={size}
        strokeWidth={1.5}
        className={cn(
          "transition-colors",
          isWatching ? "text-gold fill-gold" : "text-gray-mid"
        )}
        aria-hidden
      />
    </button>
  );
}
